import * as constants from "./constants.js";
import * as shapeFactory from "./geometry/shape-factory.js";
import * as shapeToGridConverterMaker from "./geometry/shape-to-grid-converter.js";
import forEach from "./util/for-each.js";
import * as trajectoryUtil from "./geometry/trajectory/trajectory-util.js";
import clone from "./util/clone.js";
import { CellStamps } from "./util/cell-stamps.js";
import { trackedGridBlocks, notePainted, invalidateGridBlocks } from "./util/grid-blocks.js";

var shapeToGridConverter = shapeToGridConverterMaker.createShapeToGridConverter();

function addEffect(gameState, effect) {
    effect.id = getNextId(gameState, "effect");
    gameState.effects.push(effect);
    gameState.effectEvents.push({
        type: "spawn",
        time: gameState.gameTime,
        effect: clone(effect)
    });
}

function addPlayer(gameState, {clientId, id=getNextId("player")}) {
    gameState.players.push({
        id,
        clientId,
        alive: true,
        steering: constants.STEERING_STRAIGHT,
        gameTimeWhenSteeringChanged: 0
    });
    gameState.playerSteeringSegments[id] = [];
}

function addPlayerSteeringSegment(gameState, playerId, steering, duration) {
    var steeringSegments = gameState.playerSteeringSegments[playerId];
    if (steeringSegments.length > 0 && steeringSegments[steeringSegments.length - 1].steering === steering) {
        steeringSegments[steeringSegments.length - 1].duration += duration;
    } else {
        steeringSegments.push({
            steering: steering,
            startTime: gameState.gameTime - duration,
            duration: duration
        });
    }
}

function addPowerUp(gameState, powerUp) {
    powerUp.id = getNextId(gameState, "power-up");
    gameState.powerUps.push(powerUp);
    gameState.powerUpEvents.push({
        type: "spawn",
        time: gameState.gameTime,
        powerUp: powerUp
    })
}

function createWorm(gameState, {id=getNextId(gameState, "worm"), playerId, direction=0, centerX=0, centerY=0, radius=constants.WORM_RADIUS, speed=constants.WORM_SPEED, turningSpeed=constants.WORM_TURNING_SPEED, distanceTravelled=0, distanceTravelledFromCells=new CellStamps()}) {
    return {
        id,
        playerId,
        // STABLE track key, set once at creation and never changed. A switcharoonie changes a worm's
        // OWNER (playerId) but not its identity, so its path stays under this key - only the per-
        // segment playerId (used for colour) flips. Everything that stores/looks up a worm's path
        // segments keys by worm.trackId, so ownership can move without re-keying the trail.
        trackId: playerId + "_" + id,
        centerX,
        centerY,
        radius,
        direction,
        speed,
        turningSpeed,
        alive: true,
        jump: {
            remainingJumpTime: 0,
            timeSinceLastJump: 0
        },
        distanceTravelled,
        distanceTravelledFromCells
    };
}

function createWorms(gameState, ...wormsData) {
    return wormsData.map(wd => createWorm(gameState, wd));
}

function addWorm(gameState, worm) {
    gameState.worms.push(worm);
    var wormPathSegment = createWormPathSegment(gameState, worm.id, {duration: 0, centerX: worm.centerX, centerY: worm.centerY, direction: worm.direction, speed: worm.speed, turningVelocity: 0, jump: false, size: worm.radius});
    addWormPathSegment(gameState, wormPathSegment);
    return worm;
}

function addWormPathSegment(gameState, segment) {
    var segments = gameState.wormPathSegments[segment.id];
    if (!segments) {
        segments = gameState.wormPathSegments[segment.id] = [];
    }
    if (segments.length === 0) {
        segments.push(segment);
    } else {
        var lastSegment = segments[segments.length - 1];
        if (segment.type === lastSegment.type &&
            segment.speed === lastSegment.speed &&
            segment.turningVelocity === lastSegment.turningVelocity &&
            segment.size === lastSegment.size &&
            segment.playerId === lastSegment.playerId &&
            segment.jump === lastSegment.jump &&
            segment.startDirection === lastSegment.endDirection &&
            segment.startX === lastSegment.endX &&
            segment.startY === lastSegment.endY) {

            // Continue last segment
            lastSegment.duration += segment.duration;
            lastSegment.endTime += segment.duration;
            lastSegment.endX = segment.endX;
            lastSegment.endY = segment.endY;
            lastSegment.endDirection = segment.endDirection;
            if (segment.type === "arc") {
                lastSegment.arcEndAngle = segment.arcEndAngle;
                lastSegment.arcAngleDiff += segment.arcAngleDiff;
            }
        } else {
            // Start new segment
            segments.push(segment);
        }
    }
}

function createWormPathSegment(gameState, wormId, {playerId = getPlayer(gameState, wormId).id, segmentId = ((getWorm(gameState, wormId) || {}).trackId || (playerId + "_" + wormId)), duration = 0, centerX, centerY, direction, speed, turningVelocity, jump, size} = {}) {
    var latestWormPathSegment = getLatestWormPathSegment(gameState, segmentId);
    var pathSegment = trajectoryUtil.createTrajectory({
        duration: duration,
        startX: centerX !== undefined ? centerX : latestWormPathSegment.endX,
        startY: centerY !== undefined ? centerY : latestWormPathSegment.endY,
        startDirection: direction !== undefined ? direction : latestWormPathSegment.endDirection,
        speed: speed !== undefined ? speed : latestWormPathSegment.speed,
        turningVelocity: turningVelocity !== undefined ? turningVelocity : latestWormPathSegment.turningVelocity
    });
    pathSegment.startTime = gameState.gameTime - duration;
    pathSegment.endTime = gameState.gameTime;
    pathSegment.jump = jump !== undefined ? jump : latestWormPathSegment.jump;
    pathSegment.size = size !== undefined ? size : latestWormPathSegment.size;
    pathSegment.playerId = playerId;
    pathSegment.wormId = wormId;
    pathSegment.id = segmentId;
    return pathSegment;
}

function createMap({ name, shape, borderWidth=0, blockingShapes=[] }) {
    return {
        name,
        shape,
        borderWidth,
        blockingShapes,
        width: shape.boundingBox.width + 2 * borderWidth,
        height: shape.boundingBox.height + 2 * borderWidth
    }
}

function createMapCircle({ radius, name="Circle " + radius, borderWidth=10, blockingShapes=[] }) {
    return createMap({
        name,
        shape: shapeFactory.createCircle(radius, borderWidth, borderWidth),
        borderWidth,
        blockingShapes
    });
}

function createMapRectangle({ width, height, name="Rectangle " + width + " " + height, borderWidth=10, blockingShapes=[] }) {
    return createMap({
        name,
        shape: shapeFactory.createRectangle(width, height, borderWidth, borderWidth),
        borderWidth,
        blockingShapes
    });
}

// A stadium (a pill lying along x): see shape-factory createStadium.
function createMapStadium({ width, height, name="Stadium " + width + " " + height, borderWidth=10, blockingShapes=[] }) {
    return createMap({
        name,
        shape: shapeFactory.createStadium(width, height, borderWidth, borderWidth),
        borderWidth,
        blockingShapes
    });
}

function createMapSquare({ size, name="Square " + size, borderWidth=10, blockingShapes=[] }) {
    return createMapRectangle({
        name,
        width: size,
        height: size,
        borderWidth,
        blockingShapes
    });
}

// The standard arena for a size: a landscape 2:1 rectangle with the same AREA as a
// square of side `side` (500 -> 707 x 354, 1000 -> 1414 x 707), so a size keeps its
// room while a phone wastes less screen on it.
function equalAreaRectangle(side) {
    return {
        width: Math.round(side * Math.SQRT2),
        height: Math.round(side / Math.SQRT2)
    };
}

// The standard round arena for a size: a 2:1 stadium (a pill) with the same AREA
// as a circle of diameter `diameter`. Radius r, straight part 2r, so the whole is
// 4r x 2r: pi r^2 + 4r^2 = pi (d/2)^2 (700 -> 928 x 464, 1000 -> 1327 x 663).
function equalAreaStadium(diameter) {
    var r = (diameter / 2) * Math.sqrt(Math.PI / (Math.PI + 4));
    return {
        width: Math.round(4 * r),
        height: Math.round(2 * r)
    };
}

// The diameter of the circle with the same area as this width x height stadium
// (the inverse of equalAreaStadium, up to rounding): how "big" a stadium is.
function equivalentCircleDiameter(width, height) {
    var r = height / 2;
    var area = Math.PI * r * r + 2 * r * Math.max(0, width - 2 * r);
    return 2 * Math.sqrt(area / Math.PI);
}

// The side of the square with the same area as this width x height (the inverse of
// equalAreaRectangle, up to rounding): how "big" a rectangle is, as one number.
function equivalentSquareSide(width, height) {
    return Math.sqrt(width * height);
}

// A map value string -> a core map. The value convention is "<Type> <numbers>":
// "Square <side>", "Circle <diameter>", "Rectangle <width> <height>",
// "Stadium <width> <height>". The string
// is also the map's name (what the leaderboard and replays show).
function createMapFromValue(value) {
    var parts = String(value).split(" ");
    var type = parts[0];
    var a = Number(parts[1]);
    var b = Number(parts[2]);
    if (type === "Square" && a > 0) {
        return createMapSquare({ name: value, size: a });
    }
    if (type === "Rectangle" && a > 0 && b > 0) {
        return createMapRectangle({ name: value, width: a, height: b });
    }
    if (type === "Circle" && a > 0) {
        return createMapCircle({ name: value, radius: a / 2 });
    }
    if (type === "Stadium" && a > 0 && b > 0) {
        return createMapStadium({ name: value, width: a, height: b });
    }
    throw new Error("Invalid map string: " + value);
}

function enterStartPhase(gameState, duration=constants.START_PHASE_DURATION) {
    gameState.gameEvents.push({
        type: "start_phase",
        time: gameState.gameTime,
        duration: duration
    });
}

function forEachAlivePlayer(gameState, callback) {
    gameState.players.forEach(function (player) {
        if (player.alive) {
            callback(player);
        }
    });
}

function forEachAliveWorm(gameState, callback, playerId) {
    gameState.worms.forEach(function (worm) {
        if (worm.alive && (playerId === undefined || worm.playerId === playerId)) {
            callback(worm);
        }
    });
}

function forEachAliveLatestWormPathSegment(gameState, callback, playerId) {
    var latestSegments = [];
    forEach(gameState.wormPathSegments, function (segments) {
        if (segments.length === 0) {
            return;
        }
        latestSegments.push(segments[segments.length - 1]);
    });
    latestSegments.forEach(function (latestSegment) {
        if (latestSegment.endTime !== gameState.gameTime) {
            return
        }
        if (getWorm(gameState, latestSegment.wormId).alive && (playerId === undefined || latestSegment.playerId === playerId)) {
            callback(latestSegment);
        }
    });
}

function getAlivePlayers(gameState) {
    return gameState.players.filter(p => p.alive);
}

function getAliveWorms(gameState, playerId) {
    return gameState.worms.filter(w => w.alive && (playerId === undefined || w.playerId === playerId));
}

function getLatestWormPathSegment(gameState, segmentId) {
    var segments = gameState.wormPathSegments[segmentId];
    if(!segments || segments.length === 0) {
        return null
    }
    return gameState.wormPathSegments[segmentId][segments.length - 1];
}

function getEffect(gameState, effectId) {
    return gameState.effects.find(e => e.id === effectId);
}

function getAliveEnemyWorms(gamesState, wormId) {
    return getAliveWorms(gamesState).filter(w => w.playerId !== getWorm(gamesState, wormId).playerId);
}

// The first segment of the track covering `time` (startTime <= time <= endTime),
// or undefined. A track is appended in time order, so this binary-searches for the
// last segment starting at or before `time`, then walks back to the FIRST one that
// still covers it (a zero-length death marker or a boundary shared with the previous
// segment) - the same answer as a linear find from index 0, which this replaced: the
// client samples opponent tracks by time every frame (and asks for its own head at
// the tip), so a scan from the start of a long track grew with the round.
// When the found segment ends before `time` (a gap, or past the track's end) only a
// few predecessors can still cover it: a segment's endTime can overlap its
// successor only by a stamp (the host stamps a human's latest segment forward each
// tick), never by more than a handful of segments.
var SEGMENT_OVERLAP_LOOKBACK = 8;
function getWormPathSegmentAtTime(gameState, segmentId, time) {
    var segments = gameState.wormPathSegments[segmentId];
    var lo = 0, hi = segments.length - 1, idx = -1;
    while (lo <= hi) {
        var mid = (lo + hi) >> 1;
        if (segments[mid].startTime <= time) {
            idx = mid;
            lo = mid + 1;
        } else {
            hi = mid - 1;
        }
    }
    if (idx < 0) {
        return undefined;
    }
    var found = -1;
    for (var k = idx; k >= 0 && k >= idx - SEGMENT_OVERLAP_LOOKBACK; k--) {
        if (segments[k].endTime >= time) {
            found = k;
        } else if (found >= 0) {
            break;
        }
    }
    return found >= 0 ? segments[found] : undefined;
}

function getWormPathSegmentDataAtTime(gameState, segmentId, time) {
    var segment = getWormPathSegmentAtTime(gameState, segmentId, time);
    if(!segment) {
        return null;
    }

    return {segment, position: trajectoryUtil.followTrajectory(segment, time - segment.startTime)};
}

function getPlayer(gameState, id) {
    if (id === null || id === undefined) {
        throw new Error("Id must be set");
    }
    var player = gameState.players.find(p => p.id === id);
    if (player) {
        return player;
    } else {
        var worm = getWorm(gameState, id);
        if (worm) {
            return getPlayer(gameState, worm.playerId);
        } else {
            return getPlayer(gameState, getLatestWormPathSegment(gameState, id).playerId);
        }
    }
}

function getPowerUp(gameState, powerUpId) {
    return gameState.powerUps.find(p => p.id === powerUpId);
}

function getWorm(gameState, wormId) {
    return gameState.worms.find(w => w.id === wormId);
}

// Asked many times per worm per tick (and per adopted segment), so these walk the
// effect list once instead of building filtered copies.
function getWormEffects(gameState, wormId, effectType) {
    var effects = gameState.effects;
    var out = [];
    for (var i = 0; i < effects.length; i++) {
        var e = effects[i];
        if (e.wormId === wormId && (!effectType || e.type === effectType)) {
            out.push(e);
        }
    }
    return out;
}

function getWormEffect(gameState, wormId, effectType) {
    var effects = gameState.effects;
    for (var i = 0; i < effects.length; i++) {
        var e = effects[i];
        if (e.wormId === wormId && (!effectType || e.type === effectType)) {
            return e;
        }
    }
    return null;
}

function hasWormEffect(gameState, wormId, effectType) {
    return getWormEffect(gameState, wormId, effectType) !== null;
}

function isPlayerAlive(gameState, playerId) {
    return !!getAlivePlayers(gameState).find(p => p.id === playerId);
}

function removeEffect(gameState, effectId) {
    gameState.effectEvents.push({
        type: "despawn",
        time: gameState.gameTime,
        effectId
    });
    gameState.effects.splice(gameState.effects.findIndex(effect => effect.id === effectId), 1);
}

function removePowerUp(gameState, powerUpId, despawnTime, grabbedBy) {
    gameState.powerUpEvents.push({
        type: "despawn",
        // The game-time the power-up was actually taken (the grabbing head's crossing).
        // Callers that don't know it pass nothing and fall back to now.
        time: typeof despawnTime === "number" ? despawnTime : gameState.gameTime,
        id: powerUpId,
        // Track (playerId_wormId) of the worm that took it, so the renderer can play the
        // despawn on THAT worm's render clock (an opponent's is render-delayed) and vanish
        // it exactly as their head reaches it. Undefined for callers that don't pass it.
        grabbedBy: grabbedBy
    });
    gameState.powerUps.splice(gameState.powerUps.findIndex(powerUp => powerUp.id === powerUpId), 1);
}

function resetPlayArea(gameState) {
    var grid = gameState.playArea.grid;
    invalidateGridBlocks(grid);
    for (var i = 0; i < grid.length; i++) {
        grid[i] = constants.PLAY_AREA_FREE;
    }
}

function setPlayerSteering(gameState, playerId, steering) {
    var player = getPlayer(gameState, playerId);
    if (player.steering !== steering) {
        player.gameTimeWhenSteeringChanged = gameState.gameTime;
        player.steering = steering;
    }
}

function extractReplayGameState(gameState) {
    return {
        // Keep each worm's playerId: the head renderer resolves a head's owner (colour,
        // player) from the worm's CURRENT playerId (a switcharoonie can move it), so a
        // worm stripped to just {id} renders no head/arrow during replay.
        worms: gameState.worms.map(worm => ({id: worm.id, playerId: worm.playerId})),
        players: gameState.players.map(player => ({id: player.id})),
        wormPathSegments: gameState.wormPathSegments,
        gameEvents: gameState.gameEvents,
        powerUpEvents: gameState.powerUpEvents,
        effectEvents: gameState.effectEvents,
        gameTime: gameState.gameTime,
        map: gameState.map,
        startPhaseTime: gameState.startPhaseTime,
        rollCallTime: gameState.rollCallTime || 0,
        // a Respawn round's timer and scoring (respawn.js)
        respawn: gameState.respawn
    };
}

// Incremental game-state changes since `sinceGameTime`, for streaming a live game
// to clients without resending the whole state every tick. The client starts from
// the full gameState in `game_start` and merges these deltas.
//
// Per worm track we send the segments from the first one still "open" at
// `sinceGameTime` (endTime > sinceGameTime) onward, with that start index — the
// boundary segment is resent because it may have been extended in place since the
// last send, and any newer segments are appended. Events are append-only, so we
// just send those newer than `sinceGameTime`.
//
// Client merge: wormPathSegments[trackId].splice(fromIndex, Infinity, ...segments);
// append the event arrays; set gameTime.
function getGameStateChanges(gameState, sinceGameTime) {
    var wormPathSegments = {};
    Object.keys(gameState.wormPathSegments).forEach(function (trackId) {
        var segments = gameState.wormPathSegments[trackId];
        var fromIndex = segments.findIndex(function (s) { return s.endTime > sinceGameTime; });
        if (fromIndex === -1) {
            return;
        }
        wormPathSegments[trackId] = { fromIndex: fromIndex, segments: segments.slice(fromIndex) };
    });
    return {
        gameTime: gameState.gameTime,
        wormPathSegments: wormPathSegments,
        gameEvents: gameState.gameEvents.filter(function (e) { return e.time > sinceGameTime; }),
        powerUpEvents: gameState.powerUpEvents.filter(function (e) { return e.time > sinceGameTime; }),
        effectEvents: gameState.effectEvents.filter(function (e) { return e.time > sinceGameTime; })
    };
}

// The wire form of a delta (getGameStateChanges output) for the host -> client
// stream. A segment is sent as the INPUTS that rebuild it (what
// trajectory-util.createTrajectory takes: start, heading, speed, turning,
// duration) plus its time and identity, under one-letter keys, rounded:
// positions to 0.01 units, headings to 0.0001 rad, times to 0.1ms, speeds to
// 0.01. The receiver rebuilds the full segment (end point, arc geometry, type)
// with the same createTrajectory, so both sides hold the same shape. The
// original carried 23 fields with 15-digit doubles and its own key names
// (~400 bytes a segment); a full box streams hundreds a second. The game
// state itself is never touched: only the copies sent are encoded.
var WIRE_VERSION = 2;
var WIRE_POS = 100, WIRE_DIR = 10000, WIRE_TIME = 10000, WIRE_SPEED = 100;
function roundTo(v, f) { return typeof v === "number" ? Math.round(v * f) / f : v; }
function encodeWormPathSegment(seg) {
    var w = {
        d: roundTo(seg.duration, WIRE_TIME),
        x: roundTo(seg.startX, WIRE_POS), y: roundTo(seg.startY, WIRE_POS), a: roundTo(seg.startDirection, WIRE_DIR),
        s: roundTo(seg.speed, WIRE_SPEED), w: roundTo(seg.turningVelocity, WIRE_SPEED),
        t: roundTo(seg.startTime, WIRE_TIME),
        z: roundTo(seg.size, WIRE_SPEED),
        p: seg.playerId, m: seg.wormId, i: seg.id
    };
    if (seg.jump) { w.j = 1; }
    // A death marker keeps its type; every other type follows from the inputs.
    if (seg.type === "worm_died") { w.k = seg.type; }
    return w;
}
function decodeWormPathSegment(w) {
    var seg = trajectoryUtil.createTrajectory({ startX: w.x, startY: w.y, startDirection: w.a, speed: w.s, turningVelocity: w.w, duration: w.d });
    seg.startTime = w.t;
    seg.endTime = w.t + w.d;
    seg.jump = !!w.j;
    seg.size = w.z;
    seg.playerId = w.p;
    seg.wormId = w.m;
    seg.id = w.i;
    if (w.k) { seg.type = w.k; }
    return seg;
}
// Version 3 (sent only when every client in the room understands it, see the edge
// server): each track's player and worm id are said once per track, not in every
// segment, and a segment's own id (always its track's key) is left out. A segment
// still names its player when it differs from its track's (a switcharoonie hands a
// worm to a new player mid-track). That was ~50 of a segment's ~130 bytes.
var WIRE_VERSION_COMPACT = 3;
function encodeWormPathSegmentIn(seg, trackPlayerId, trackWormId, trackId) {
    var w = {
        d: roundTo(seg.duration, WIRE_TIME),
        x: roundTo(seg.startX, WIRE_POS), y: roundTo(seg.startY, WIRE_POS), a: roundTo(seg.startDirection, WIRE_DIR),
        s: roundTo(seg.speed, WIRE_SPEED), w: roundTo(seg.turningVelocity, WIRE_SPEED),
        t: roundTo(seg.startTime, WIRE_TIME),
        z: roundTo(seg.size, WIRE_SPEED)
    };
    if (seg.playerId !== trackPlayerId) { w.p = seg.playerId; }
    if (seg.wormId !== trackWormId) { w.m = seg.wormId; }
    if (seg.id !== trackId) { w.i = seg.id; }
    if (seg.jump) { w.j = 1; }
    if (seg.type === "worm_died") { w.k = seg.type; }
    return w;
}
function encodeGameStateChanges(data, version) {
    var compact = version === WIRE_VERSION_COMPACT;
    var tracks = {};
    Object.keys(data.wormPathSegments || {}).forEach(function (trackId) {
        var t = data.wormPathSegments[trackId];
        if (compact) {
            var first = t.segments[0] || {};
            tracks[trackId] = { fromIndex: t.fromIndex, p: first.playerId, m: first.wormId, segments: t.segments.map(function (seg) {
                return encodeWormPathSegmentIn(seg, first.playerId, first.wormId, trackId);
            }) };
        } else {
            tracks[trackId] = { fromIndex: t.fromIndex, segments: t.segments.map(encodeWormPathSegment) };
        }
    });
    var out = {};
    for (var k in data) { out[k] = data[k]; }
    out.wire = compact ? WIRE_VERSION_COMPACT : WIRE_VERSION;
    out.wormPathSegments = tracks;
    return out;
}
// The delta as the receiver uses it. A delta that was not encoded (an older
// host, a local harness) passes through untouched.
function decodeGameStateChanges(data) {
    if (!data || (data.wire !== WIRE_VERSION && data.wire !== WIRE_VERSION_COMPACT)) { return data; }
    var compact = data.wire === WIRE_VERSION_COMPACT;
    var tracks = {};
    Object.keys(data.wormPathSegments || {}).forEach(function (trackId) {
        var t = data.wormPathSegments[trackId];
        tracks[trackId] = { fromIndex: t.fromIndex, segments: t.segments.map(function (w) {
            var seg = decodeWormPathSegment(w);
            if (compact) {
                if (w.p === undefined) { seg.playerId = t.p; }
                if (w.m === undefined) { seg.wormId = t.m; }
                if (w.i === undefined) { seg.id = trackId; }
            }
            return seg;
        }) };
    });
    var out = {};
    for (var k in data) { out[k] = data[k]; }
    delete out.wire;
    out.wormPathSegments = tracks;
    return out;
}

function getNextId(gameState, prefix = "") {
    var nextId = prefix + "_" + gameState.nextId;
    gameState.nextId += 1;
    return nextId;
}

function isRoundMap(gameState) {
    return gameState.map.shape.type === "circle";
}

function addPlayAreaShape(gameState, shape, value) {
    var playArea = gameState.playArea;
    var points = shapeToGridConverter.convert(shape, playArea);
    var changedData = [];
    var blocks = trackedGridBlocks(playArea.grid); // bots' coarse summary, if one is kept
    for (var i = 0; i < points.length; i++) {
        // Buffer should only be updated when a value has changed
        // TODO ATM we do not accept "painting over". If we were, worm worm collision would have issues since we would not collide against other worms, might want to fix this
        if (playArea.grid[points[i]] !== value) {
            if (blocks && playArea.grid[points[i]] === constants.PLAY_AREA_FREE) {notePainted(blocks, points[i]);}
            playArea.grid[points[i]] = value;
            changedData.push({
                index: points[i],
                value
            });
        }
    }
    return changedData;
}

function getStartPhaseTimeLeft(gameState) {
    return gameState.startPhaseTime - gameState.gameTime;
}

// The countdown proper (the start phase after the roll call), in seconds,
// rounded to the millisecond. startPhaseTime is the roll call plus the
// countdown, so subtracting the roll call back out lands on e.g.
// 3.0000000000000004, whose ceil is 4: the audio counted one beat early.
function getCountdownLength(gameState) {
    return Math.round(((gameState.startPhaseTime || 0) - (gameState.rollCallTime || 0)) * 1000) / 1000;
}

function addPlayAreaObstacle(gameState, shape) {
    return addPlayAreaShape(gameState, shape, constants.PLAY_AREA_OBSTACLE);
}

function isInStartPhase(gameState) {
    return gameState.gameActive && gameState.gameTime < gameState.startPhaseTime;
}

function isInPlayPhase(gameState) {
    return gameState.gameActive && !isInStartPhase(gameState);
}

function createGameState({
    map,
    seed = 0,
    players = [],
    worms = [],
    powerUps = [],
    effects = [],
    missiles = [],
    playerSteeringSegments = {},
    wormPathSegments = {},
    gameEvents = [],
    powerUpEvents = [],
    effectEvents = [],
    gameTime = 0,
    gameActive = false,                                  // TODO: might get removed
    startPhaseTime = 3,
    rollCallTime = 0,                                    // the roll call's share of the start phase (the countdown proper is the rest)
    disabledEffectTypes = [],
    disabledPowerUps = [],
    powerUpSpawnChance = constants.POWER_UP_SPAWN_CHANCE,
    crowdLevel,                                          // the match's bot level when the host decided one (ai/crowd.js); undefined = by roster
    powerUpGreedValues,                                  // the host's retuned power-up values for bots' greed (ai/powerup-values.js); undefined = defaults
    nextId = 0
    } = {}) {
    function createPlayArea(width, height) {
        var playArea = {
            rows: height,
            cols: width,
            grid: new Array(height * width)
        };
        for (var i = 0; i < playArea.grid.length; i++) {
            playArea.grid[i] = constants.PLAY_AREA_FREE;
        }
        return playArea;
    }

    return {
        players,
        //  [{
        //      id,
        //      steering,
        //      gameTimeWhenSteeringChanged,
        //      alive
        //  }]
        worms,
        //  [{
        //      id,
        //      playerId,
        //      centerX,
        //      centerY,
        //      radius,
        //      direction,
        //      speed,
        //      turningSpeed,
        //      alive,
        //      jump: {
        //          remainingJumpTime: 0,
        //          timeSinceLastJump: 0
        //      },
        //      immunityData: undefined
        //      }]

        powerUps,
        //  [{
        //      id,
        //      name,
        //      shape,
        //      effectType,
        //      effectStrength, // Higher means more potent, negative could mean reversed. For speed effect, -1 means decreased speed for example
        //      effectDuration, // The duration for the effect, if it has one
        //      affects         // all | others | self
        //  }]

        effects,
        //  [{
        //      type,
        //      remainingDuration,
        //      wormId,
        //      strength            // Comes from the power-ups effectStrength
        //  }]
        missiles,
        //  Red power-up deliveries in flight, SIMULATED: each pursues its victim's live
        //  head at RED_MISSILE_SPEED and applies its effect on the actual catch (see
        //  core-functions activatePowerUp + updateMissiles). Fleeing a missile genuinely
        //  buys time. Present (as []) on every live game; reconstructed replay states
        //  lack it, which is how the renderer picks its missile source.
        //  [{
        //      id,             // powerUpId + "_" + victim wormId
        //      x, y,           // current position (game units)
        //      radius,         // the token's radius (visual size)
        //      targetWormId,
        //      effect          // the cloned effect to apply on catch
        //  }]
        playerSteeringSegments,
        //  {[
        //      steering,
        //      startTime,
        //      duration
        //  ]}
        wormPathSegments,
        //  {[
        //      type,           // straight | arc | still_arc
        //      index,          // the position of the segment in the list
        //      duration,
        //      startTime,
        //      endTime,
        //      jump,           // true | false
        //      size,
        //      playerId,
        //      startX,
        //      startY,
        //      startDirection,
        //      speed,
        //      turningVelocity
        //      endX,
        //      endY,
        //      endDirection
        //  ]}
        gameEvents,
        //  [{
        //      type,           // game_start | player_died | game_over
        //      time
        //      (id)            // Only for type player_died
        //  }],
        powerUpEvents,
        //  [{
        //      type,           // spawn | despawn
        //      time,
        //      (powerUp),      // Only for type spawn
        //      (id)            // Only for type despawn
        //  }],
        effectEvents,
        //  [{
        //      type,
        //      time
        //      (effect)        // Only for type spawn
        //      (id)            // Only for type despawn
        //  }],
        map,
        playArea: map ? createPlayArea(map.width, map.height) : null,
        gameTime,
        gameActive, // TODO: might get removed
        startPhaseTime, // How long the start phase lasts (roll call + countdown)
        rollCallTime,   // ... of which the first rollCallTime seconds are the roll call
        // Power-up spawn config (local play's settings panel; defaults preserve
        // the classic behaviour, and online leaves them unset).
        disabledEffectTypes, // effect types never spawned (by effectType)
        disabledPowerUps,    // individual power-ups never spawned (by definition key)
        powerUpSpawnChance,  // per-second-ish spawn chance; 0 = no power-ups
        crowdLevel,          // explicit bot level for this match (ai/crowd.js resolveCrowdLevel); undefined = the roster decides
        powerUpGreedValues,  // per-power-up worth to a greedy bot, when the host retuned it (ai/powerup-values.js)
        seed,
        nextId
    }
}

export {
    addEffect,
    addPlayAreaObstacle,
    addPlayAreaShape,
    addPlayer,
    addPlayerSteeringSegment,
    addPowerUp,
    addWorm,
    addWormPathSegment,
    createWormPathSegment,
    createGameState,
    createMap,
    createMapCircle,
    createMapRectangle,
    createMapSquare,
    createMapFromValue,
    createMapStadium,
    equalAreaRectangle,
    equalAreaStadium,
    equivalentCircleDiameter,
    equivalentSquareSide,
    createWorm,
    createWorms,
    enterStartPhase,
    extractReplayGameState,
    forEachAlivePlayer,
    forEachAliveWorm,
    forEachAliveLatestWormPathSegment,
    getGameStateChanges,
    encodeGameStateChanges,
    WIRE_VERSION_COMPACT,
    decodeGameStateChanges,
    encodeWormPathSegment,
    decodeWormPathSegment,
    getAliveEnemyWorms,
    getAlivePlayers,
    getAliveWorms,
    getEffect,
    getLatestWormPathSegment,
    getWormPathSegmentAtTime,
    getWormPathSegmentDataAtTime,
    getNextId,
    getPowerUp,
    getPlayer,
    getStartPhaseTimeLeft,
    getCountdownLength,
    getWorm,
    getWormEffect,
    getWormEffects,
    hasWormEffect,
    isInStartPhase,
    isInPlayPhase,
    isPlayerAlive,
    isRoundMap,
    removeEffect,
    removePowerUp,
    resetPlayArea,
    setPlayerSteering
};